import type { MenuItem } from '../services/backend';

type MenuPlacement = 'header' | 'footer' | 'topbar';

export const normalizeMenuUrl = (value?: string | null) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  return trimmed.endsWith('/') && trimmed.length > 1 ? trimmed.slice(0, -1) : trimmed;
};

const normalizeParentId = (value?: string | null) => (value || '').trim() || '__root__';

const getMenuPlacements = (type: MenuItem['type']): MenuPlacement[] => {
  if (type === 'both') return ['header', 'footer'];
  if (type === 'topbar') return ['topbar'];
  return [type];
};

const getMenuDedupKey = (item: Pick<MenuItem, 'url' | 'parentId'>, placement: MenuPlacement) =>
  `${placement}::${normalizeParentId(item.parentId)}::${normalizeMenuUrl(item.url)}`;

export const menuItemsConflict = (
  left: Pick<MenuItem, 'url' | 'type' | 'parentId'>,
  right: Pick<MenuItem, 'url' | 'type' | 'parentId'>
) => {
  const leftPlacements = getMenuPlacements(left.type);
  const rightPlacements = getMenuPlacements(right.type);
  const overlappingPlacements = leftPlacements.filter((placement) => rightPlacements.includes(placement));

  if (overlappingPlacements.length === 0) return false;

  return overlappingPlacements.some(
    (placement) => getMenuDedupKey(left, placement) === getMenuDedupKey(right, placement)
  );
};

export const dedupeMenuItems = (items: MenuItem[], placement?: MenuPlacement) => {
  const seen = new Set<string>();

  return [...items]
    .sort((left, right) => left.order - right.order)
    .filter((item) => {
      const placements = getMenuPlacements(item.type);
      const relevantPlacements = placement
        ? placements.filter((currentPlacement) => currentPlacement === placement)
        : placements;

      if (relevantPlacements.length === 0) return true;

      const keys = relevantPlacements.map((currentPlacement) =>
        getMenuDedupKey(item, currentPlacement)
      );

      if (keys.some((key) => seen.has(key))) {
        return false;
      }

      keys.forEach((key) => seen.add(key));
      return true;
    });
};

export type DefaultFooterLink = {
  label: string;
  url: string;
  order: number;
};

export const DEFAULT_FOOTER_LINKS: DefaultFooterLink[] = [
  { label: 'Sobre a Veraz', url: '/sobre', order: 10 },
  { label: 'Contato', url: '/contato', order: 20 },
  { label: 'Rastreio', url: '/rastreio', order: 30 },
  { label: 'Trocas e devoluções', url: '/trocas', order: 40 },
  { label: 'Seja um revendedor', url: '/seja-um-revendedor', order: 50 },
  { label: 'Política de privacidade', url: '/politica-de-privacidade', order: 60 },
  { label: 'Blog', url: '/blog', order: 70 },
];

export const DEFAULT_HEADER_MENU_ITEMS: Omit<MenuItem, 'id'>[] = [
  { label: 'INÍCIO', url: '/', type: 'header', order: 1, parentId: null },
  { label: 'PRODUTOS', url: '/produtos', type: 'header', order: 2, parentId: null },
  { label: 'KITS', url: '/kits', type: 'header', order: 3, parentId: null },
  { label: 'EQUIPAMENTOS', url: '/equipamentos', type: 'header', order: 4, parentId: null },
];

export const DEFAULT_FOOTER_MENU_ITEMS: Omit<MenuItem, 'id'>[] = DEFAULT_FOOTER_LINKS.map((link) => ({
  label: link.label,
  url: link.url,
  type: 'footer',
  order: link.order,
  parentId: null,
}));
